/**
 * Elevation tokens — les ombres du design system (Brand core V13, chap. 6 et 13).
 *
 * Le livre n'en chiffre qu'UNE, celle de la carte : `0 6px 18px rgba(15,27,61,.06)`. Elle ne
 * classe pas les surfaces, elle DESSINE la carte — une carte V13 est blanche, sans filet, et
 * c'est cette ombre très basse qui lui donne son bord sur la page `#F5F6F8`. Les deux marches de
 * la V10 (`soft`, `raised`) disparaissent avec la hiérarchie qu'elles portaient.
 *
 * Les trois suivantes ne sont pas des marches de cette échelle : ce sont les ombres des surfaces
 * qui FLOTTENT au-dessus de la page — une feuille, un bandeau d'accusé, un menu. Elles étaient
 * écrites à la main, un jeu de nombres dans chaque composant ; ici elles sont nommées par ce
 * qu'elles portent, et un composant ne choisit plus que le nom.
 *
 * Volontairement **sans couleur** : `utils/shadow` lit le jeton `shadow` lui-même, et ne garde
 * d'ici que les nombres. Une valeur numérique traverse la conversion Unistyles → variables CSS
 * sans transformation, alors qu'une couleur écrite ici deviendrait une variable de plus,
 * détachée de la palette.
 *
 * Marche à suivre dans un composant : `...shadowStyle(theme, "card")`.
 *
 * `elevation` (Android) n'accepte pas d'ombre colorée : c'est la marche qui porte la profondeur.
 * Sur le web, react-native-web traduit les quatre `shadow*` en `box-shadow`.
 */
export const elevation = {
    /**
     * Aucune ombre — une surface ENCASTRÉE : une ligne d'option dans une liste déroulante, un
     * bloc teinté à l'intérieur d'une carte. Elle n'est pas posée sur la page, elle est dedans.
     */
    none: { offsetY: 0, opacity: 0, radius: 0, android: 0 },
    /** L'ombre de la carte — la seule du livre, et le défaut de `<Card>`. */
    card: { offsetY: 6, opacity: 0.06, radius: 18, android: 2 },
    /**
     * La feuille qui monte du bas (`<ActionSheet>`) : son ombre part vers le HAUT, du côté par
     * lequel elle recouvre la page. C'est la seule à décalage négatif.
     */
    sheet: { offsetY: -2, opacity: 0.1, radius: 8, android: 8 },
    /** Le bandeau d'accusé posé au-dessus du contenu (`<Snackbar>`) — il flotte, il ne colle pas. */
    floating: { offsetY: 8, opacity: 0.1, radius: 12, android: 6 },
    /** Le menu déroulant (`<ThreeDotMenu>`) — détaché de la ligne qui l'ouvre, sans planer. */
    menu: { offsetY: 2, opacity: 0.15, radius: 8, android: 4 },
} as const

export type ElevationKey = keyof typeof elevation

/** Les quatre nombres d'une marche — la forme que `utils/shadow` consomme. */
export type ElevationStep = { offsetY: number; opacity: number; radius: number; android: number }
